// ============================================
// ADVANCED PROMPT ENGINEERING SYSTEM
// Toggle this flag to enable/disable
// ============================================
export const USE_ADVANCED_PROMPTS = true; // ← Set to false to disable

// ============================================
// QUALITY & RESOLUTION DESCRIPTORS
// ============================================
const QUALITY_MODIFIERS = [
  "photorealistic",
  "hyperrealistic",
  "ultrarealistic",
  "8k",
  "8k ultra high resolution",
  "high resolution",
  "maximum detail",
  "extreme detail",
  "intricate detail",
  "meticulous detail",
  "exquisite detail",
  "fine detail",
  "crisp",
  "crystal clear",
  "tack sharp",
  "razor sharp",
  "pin sharp",
  "professional photography",
  "professional interior photography",
  "architectural photography",
  "interior architectural photography",
  "editorial photography",
  "editorial interior photography",
  "magazine editorial",
  "magazine quality",
  "magazine editorial quality",
  "architectural digest quality",
  "elle decor quality",
  "dwell magazine aesthetic",
  "veranda magazine style",
  "luxe interiors magazine",
  "interior design magazine",
  "design publication quality",
  "coffee table book quality",
  "monograph worthy",
  "portfolio grade",
  "museum grade",
  "museum quality detail",
  "gallery quality",
  "exhibition quality",
  "award winning",
  "award winning interior design",
  "pritzker worthy architecture",
  "masterwork execution",
  "masterpiece level",
  "flawless execution",
  "pristine condition",
  "immaculate presentation",
];

// ============================================
// LIGHTING DESCRIPTORS
// ============================================
const LIGHTING_MODIFIERS = [
  "natural light", "natural daylight", "sunlight", "sunlit", "sun drenched",
  "morning light", "golden hour", "golden hour glow", "twilight",
  "soft diffused light", "diffused natural light", "dappled light",
  "volumetric light", "ambient illumination", "warm ambient glow",
  "layered lighting", "architectural lighting", "recessed lighting",
  "cove lighting", "accent lighting", "statement pendant light",
  "chandelier illumination", "wall sconce glow", "candlelight ambiance",
  "chiaroscuro interior lighting", "moody lighting", "atmospheric"
];

// ============================================
// DESIGN PRINCIPLES & COMPOSITION
// ============================================
const DESIGN_PRINCIPLES_MODIFIERS = [
  "proportion", "scale", "harmonious proportion", "golden ratio",
  "symmetry", "balanced asymmetry", "rhythm", "repetition",
  "textural contrast", "material contrast", "focal point emphasis",
  "visual hierarchy", "visual balance", "cohesive design",
  "layered depth", "negative space", "open plan flow",
  "intimate seating grouping", "sight line connection",
  "vertical emphasis", "horizontality", "curvilinear",
  "rectilinear", "sculptural volume", "human scale"
];

// ============================================
// OBJECT WORD LIBRARY (all entries labeled: "category: term")
// ============================================

// --- COLOR (labeled) ---
const COLOR_LIBRARY = [
  "color: pure white", "color: warm white", "color: cool white",
  "color: off white", "color: ivory", "color: cream",
  "color: beige", "color: greige", "color: taupe",
  "color: sand", "color: stone", "color: putty",
  "color: charcoal gray", "color: dove gray", "color: slate gray",
  "color: pewter", "color: silver", "color: platinum",
  "color: true black", "color: soft black", "color: jet black",
  "color: carbon black", "color: ink black", "color: espresso",
  "color: chocolate brown", "color: walnut", "color: mahogany",
  "color: cognac", "color: camel", "color: tan",
  "color: caramel", "color: terracotta", "color: rust",
  "color: ochre", "color: mustard", "color: gold",
  "color: brass", "color: copper", "color: bronze",
  "color: champagne", "color: navy blue", "color: indigo",
  "color: midnight blue", "color: royal blue", "color: cobalt",
  "color: cerulean", "color: sky blue", "color: powder blue",
  "color: slate blue", "color: teal", "color: peacock",
  "color: turquoise", "color: aqua", "color: emerald green",
  "color: forest green", "color: olive green", "color: sage",
  "color: eucalyptus", "color: celadon", "color: mint",
  "color: seafoam", "color: pistachio", "color: lime",
  "color: chartreuse", "color: crimson", "color: burgundy",
  "color: oxblood", "color: ruby", "color: garnet",
  "color: terracotta red", "color: brick red", "color: coral",
  "color: blush", "color: dusty rose", "color: mauve",
  "color: magenta", "color: fuchsia", "color: berry",
  "color: plum", "color: aubergine", "color: lavender",
  "color: lilac", "color: periwinkle", "color: amethyst",
  "color: violet", "color: purple", "color: tangerine",
  "color: apricot", "color: peach", "color: salmon",
  "color: pumpkin", "color: amber", "color: butter yellow",
  "color: lemon", "color: marigold", "color: saffron",
  "color: honey", "color: straw", "color: alabaster",
  "color: rose gold", "color: oil‑rubbed bronze",
  "color: brushed nickel", "color: satin brass",
  "color: antique brass", "color: polished chrome"
];

// --- TEXTURE / MATERIAL (labeled) ---
const TEXTURE_MATERIAL_LIBRARY = [
  "material: honed marble", "material: polished marble", "material: leathered marble",
  "material: calacatta marble", "material: carrara marble", "material: travertine",
  "material: limestone", "material: soapstone", "material: slate",
  "material: terrazzo", "material: polished concrete", "material: board‑formed concrete",
  "material: venetian plaster", "material: tadelakt plaster", "material: limewash",
  "material: white oak", "material: european oak", "material: fumed oak",
  "material: cerused oak", "material: walnut", "material: teak",
  "material: rosewood", "material: wenge", "material: bamboo",
  "material: rattan", "material: cane webbing", "material: seagrass",
  "material: jute", "material: sisal", "material: belgian linen",
  "material: linen slub", "material: velvet", "material: silk velvet",
  "material: mohair velvet", "material: bouclé", "material: bouclé wool",
  "material: shearling", "material: full‑grain leather", "material: aniline leather",
  "material: semi‑aniline leather", "material: suede", "material: nubuck",
  "material: brushed brass", "material: satin brass", "material: unlacquered brass",
  "material: polished nickel", "material: brushed stainless steel",
  "material: blackened steel", "material: corten steel",
  "material: high‑gloss lacquer", "material: ultra‑matte lacquer",
  "texture: hand‑troweled plaster", "texture: wire‑brushed wood",
  "texture: hand‑scraped wood", "texture: distressed leather",
  "texture: quilted upholstery", "texture: diamond tufting",
  "texture: channel tufting", "texture: biscuit tufting",
  "texture: fluted glass", "texture: reeded glass",
  "texture: ribbed glass", "texture: sandblasted glass",
  "texture: etched glass", "texture: chiseled stone",
  "texture: split‑face stone", "texture: bush‑hammered concrete"
];

// --- SOFA SHAPE / SEATING TYPOLOGY (labeled) ---
const SOFA_SHAPE_LIBRARY = [
  "sofa shape: singular sofa", "sofa shape: modular sofa",
  "sofa shape: L‑shaped sectional", "sofa shape: U‑shaped sectional",
  "sofa shape: curved sectional", "sofa shape: pit sectional",
  "sofa shape: chaise sectional", "sofa shape: tuxedo sofa",
  "sofa shape: camelback sofa", "sofa shape: chesterfield sofa",
  "sofa shape: lawson sofa", "sofa shape: english roll arm sofa",
  "sofa shape: track arm sofa", "sofa shape: shelter arm sofa",
  "sofa shape: tight back sofa", "sofa shape: pillow back sofa",
  "sofa shape: bench seat sofa", "sofa shape: single cushion sofa",
  "sofa shape: chaise lounge", "sofa shape: settee",
  "sofa shape: loveseat", "sofa shape: daybed",
  "sofa shape: recamier", "sofa shape: méridienne",
  "sofa shape: divan", "sofa shape: ottoman",
  "sofa shape: pouf", "sofa shape: sofa bed",
  "sofa shape: cloud sofa", "sofa shape: bubble sofa",
  "sofa shape: curved back sofa", "sofa shape: scroll arm sofa",
  "sofa shape: wingback sofa", "sofa shape: high back sofa",
  "sofa shape: low back sofa", "sofa shape: deep seat sofa",
  "sofa shape: channel back sofa", "sofa shape: tufted back sofa",
  "sofa shape: slipcovered sofa", "sofa shape: sculptural sofa",
  "sofa shape: monolithic sofa", "sofa shape: modular block sofa",
  "sofa shape: pit sectional with back cushions",
  "sofa shape: curved sectional sofa", "sofa shape: serpentine sofa",
  "sofa shape: crescent sofa", "sofa shape: round sofa",
  "sofa shape: asymmetric sofa", "sofa shape: deconstructed sofa",
  "sofa shape: origami sofa", "sofa shape: armless sofa",
  "sofa shape: one‑arm sofa", "sofa shape: bumper sectional",
  "sofa shape: wedge sectional", "sofa shape: cantilever sofa",
  "sofa shape: conversation pit sofa", "sofa shape: sunken sofa",
  "sofa shape: tuxedo sectional", "sofa shape: chesterfield sectional",
  "sofa shape: mid‑century sofa with tapered legs",
  "sofa shape: retro rounded back sofa",
  "sofa shape: 70s low‑profile sofa",
  "sofa shape: bauhaus cuboid sofa",
  "sofa shape: de stijl rectilinear sofa",
  "sofa shape: memphis geometric sofa",
  "sofa shape: postmodern playful sofa",
  "sofa shape: japandi low sofa",
  "sofa shape: wabi sabi organic sofa",
  "sofa shape: minimalist platform sofa",
  "sofa shape: brutalis monolithic sofa"
];

// --- FURNITURE TYPE (chairs, tables, storage, etc., labeled) ---
const FURNITURE_TYPE_LIBRARY = [
  "furniture: parsons table", "furniture: trestle table",
  "furniture: pedestal table", "furniture: tulip table",
  "furniture: noguchi table", "furniture: eames lounge chair",
  "furniture: barcelona chair", "furniture: wassily chair",
  "furniture: cesca chair", "furniture: panton chair",
  "furniture: wishbone chair", "furniture: shell chair",
  "furniture: butterfly chair", "furniture: club chair",
  "furniture: wingback chair", "furniture: adirondack chair",
  "furniture: barstool", "furniture: counter stool",
  "furniture: dining chair", "furniture: dining table",
  "furniture: extendable table", "furniture: drop‑leaf table",
  "furniture: console table", "furniture: demilune table",
  "furniture: coffee table", "furniture: cocktail table",
  "furniture: end table", "furniture: side table",
  "furniture: nesting tables", "furniture: c‑table",
  "furniture: gueridon", "furniture: nightstand",
  "furniture: dresser", "furniture: chest of drawers",
  "furniture: tallboy", "furniture: commode",
  "furniture: vanity", "furniture: writing desk",
  "furniture: secretary desk", "furniture: executive desk",
  "furniture: standing desk", "furniture: platform bed",
  "furniture: panel bed", "furniture: sleigh bed",
  "furniture: four‑poster bed", "furniture: canopy bed",
  "furniture: upholstered bed", "furniture: wingback bed",
  "furniture: storage bed", "furniture: murphy bed",
  "furniture: daybed", "furniture: trundle bed",
  "furniture: upholstered headboard", "furniture: tufted headboard",
  "furniture: wall mirror", "furniture: floor mirror",
  "furniture: leaning mirror", "furniture: arched mirror",
  "furniture: credenza", "furniture: sideboard",
  "furniture: buffet", "furniture: hutch",
  "furniture: breakfront", "furniture: armoire",
  "furniture: wardrobe", "furniture: walk‑in closet system",
  "furniture: rolling bar cart", "furniture: étagère",
  "furniture: bookcase with ladder", "furniture: floating shelf",
  "furniture: modular shelving", "furniture: wall‑mounted desk",
  "furniture: fold‑down desk", "furniture: drafting table",
  "furniture: easel stand", "furniture: music stand",
  "furniture: plant stand", "furniture: umbrella stand",
  "furniture: coat rack", "furniture: hall tree",
  "furniture: shoe rack", "furniture: bench with storage",
  "furniture: window seat", "furniture: banquette seating",
  "furniture: ottoman with storage", "furniture: pouf ottoman",
  "furniture: storage ottoman bench", "furniture: lift‑top coffee table"
];

// --- LIGHTING FIXTURE TYPE (labeled) ---
const LIGHTING_FIXTURE_LIBRARY = [
  "lighting: pendant light", "lighting: mini pendant",
  "lighting: multi‑light pendant", "lighting: drum pendant",
  "lighting: globe pendant", "lighting: cone pendant",
  "lighting: linear pendant", "lighting: island pendant",
  "lighting: cluster pendant", "lighting: sputnik chandelier",
  "lighting: crystal chandelier", "lighting: beaded chandelier",
  "lighting: capiz shell chandelier", "lighting: rattan chandelier",
  "lighting: iron chandelier", "lighting: wagon wheel chandelier",
  "lighting: lantern chandelier", "lighting: empire chandelier",
  "lighting: venetian chandelier", "lighting: murano chandelier",
  "lighting: contemporary chandelier", "lighting: modern chandelier",
  "lighting: flush mount", "lighting: semi‑flush mount",
  "lighting: ceiling light", "lighting: dome light",
  "lighting: schoolhouse light", "lighting: globe light",
  "lighting: cage light", "lighting: recessed light",
  "lighting: gimbal recessed", "lighting: shower recessed",
  "lighting: wafer light", "lighting: LED panel",
  "lighting: troffer", "lighting: linear fixture",
  "lighting: strip light", "lighting: cove light",
  "lighting: tape light", "lighting: rope light",
  "lighting: neon flex", "lighting: picture light",
  "lighting: art light", "lighting: bookcase light",
  "lighting: undercabinet light", "lighting: puck light",
  "lighting: task light", "lighting: floor lamp",
  "lighting: torchiere", "lighting: arc lamp",
  "lighting: pharmacy lamp", "lighting: tripod floor lamp",
  "lighting: tree floor lamp", "lighting: bridge lamp",
  "lighting: swing arm floor lamp", "lighting: table lamp",
  "lighting: desk lamp", "lighting: banker's lamp",
  "lighting: piano lamp", "lighting: buffet lamp",
  "lighting: ginger jar lamp", "lighting: gourd lamp",
  "lighting: column lamp", "lighting: ceramic lamp",
  "lighting: crystal lamp", "lighting: glass lamp",
  "lighting: metal lamp", "lighting: wood lamp",
  "lighting: marble lamp", "lighting: resin lamp",
  "lighting: acrylic lamp", "lighting: rattan lamp",
  "lighting: wicker lamp", "lighting: bamboo lamp",
  "lighting: paper lamp", "lighting: fabric lamp",
  "lighting: tiffany lamp", "lighting: stained glass lamp",
  "lighting: art glass lamp", "lighting: scissor arm lamp",
  "lighting: magnetic track light", "lighting: linear track light",
  "lighting: flexible track light", "lighting: wall sconce",
  "lighting: swing arm sconce", "lighting: picture sconce",
  "lighting: candle sconce", "lighting: outdoor lantern",
  "lighting: post light", "lighting: bollard light",
  "lighting: step light", "lighting: deck light",
  "lighting: string light", "lighting: festoon light",
  "lighting: bistro light", "lighting: pendant cord set"
];

// --- WINDOW SHAPE / TYPE (labeled) ---
const WINDOW_LIBRARY = [
  "window: casement window", "window: double‑hung window",
  "window: awning window", "window: hopper window",
  "window: fixed picture window", "window: palladian window",
  "window: venetian window", "window: round window",
  "window: porthole window", "window: bay window",
  "window: bow window", "window: oriel window",
  "window: dormer window", "window: gable dormer",
  "window: shed dormer", "window: eyebrow dormer",
  "window: transom window", "window: sidelight",
  "window: clerestory window", "window: clerestory band",
  "window: roof lantern", "window: oculus window",
  "window: arched window", "window: roman arch window",
  "window: gothic arch window", "window: tudor arch window",
  "window: ellipse arch window", "window: horseshoe arch window",
  "window: floor‑to‑ceiling window", "window: corner window",
  "window: steel frame industrial glazing", "window: critall style grid",
  "window: divided light window", "window: stained glass window",
  "window: leaded glass window", "window: beveled glass window",
  "window: etched glass window", "window: frosted glass window",
  "window: fluted glass partition", "window: reeded glass panel",
  "window: glass block window", "window: jalousie window",
  "window: sliding window", "window: tilt‑and‑turn window",
  "window: skylight", "window: flat roof skylight",
  "window: ridge skylight", "window: tubular skylight",
  "window: walk‑on skylight", "window: pyramid skylight"
];

// --- DOOR SHAPE / TYPE (labeled) ---
const DOOR_LIBRARY = [
  "door: french door", "door: french door with transom",
  "door: pocket door", "door: sliding barn door",
  "door: bypass door", "door: pivot door",
  "door: dutch door", "door: arched door",
  "door: panel door", "door: flush door",
  "door: glass door", "door: steel frame door",
  "door: critall door", "door: bi‑fold door",
  "door: accordion door", "door: sliding patio door",
  "door: multi‑slide door", "door: folding glass wall",
  "door: concealed door", "door: bookcase hidden door",
  "door: mirrored door", "door: louvered door",
  "door: chalkboard door", "door: reclaimed wood door",
  "door: iron entry door", "door: arched double entry door",
  "door: pivot entry door", "door: custom paneled door",
  "door: beadboard door", "door: shaker door",
  "door: raised panel door", "door: flat panel door",
  "door: glass‑panel pocket door", "door: frosted glass door",
  "door: fluted glass door", "door: reeded glass door",
  "door: blackened steel door", "door: brass inlay door",
  "door: chevron wood door", "door: herringbone wood door"
];

// --- CEILING SHAPE / TYPE (labeled) ---
const CEILING_LIBRARY = [
  "ceiling: flat ceiling", "ceiling: coffered ceiling",
  "ceiling: barrel vault ceiling", "ceiling: groin vault ceiling",
  "ceiling: rib vault", "ceiling: fan vault",
  "ceiling: tray ceiling", "ceiling: recessed ceiling",
  "ceiling: cove ceiling", "ceiling: beamed ceiling",
  "ceiling: exposed timber beam ceiling", "ceiling: steel beam ceiling",
  "ceiling: tongue and groove ceiling", "ceiling: vaulted ceiling",
  "ceiling: cathedral ceiling", "ceiling: shed ceiling",
  "ceiling: butterfly ceiling", "ceiling: dome ceiling",
  "ceiling: cupola", "ceiling: oculus",
  "ceiling: latticed ceiling", "ceiling: trellis ceiling",
  "ceiling: acoustic panel ceiling", "ceiling: slat wood ceiling",
  "ceiling: stretched fabric ceiling", "ceiling: backlit stretch ceiling",
  "ceiling: mirrored ceiling", "ceiling: tin ceiling",
  "ceiling: plaster medallion ceiling", "ceiling: ornate plaster ceiling",
  "ceiling: geometric coffered ceiling", "ceiling: stepped tray ceiling",
  "ceiling: curved soffit ceiling", "ceiling: wood‑clad ceiling",
  "ceiling: painted beadboard ceiling", "ceiling: double‑height ceiling"
];

// --- WALL MOULDING / PANELING (labeled) ---
const MOULDING_LIBRARY = [
  "moulding: crown moulding", "moulding: dentil crown moulding",
  "moulding: egg and dart moulding", "moulding: ogee moulding",
  "moulding: cove moulding", "moulding: baseboard",
  "moulding: tall baseboard", "moulding: picture rail",
  "moulding: chair rail", "moulding: plate rail",
  "moulding: cornice", "moulding: frieze",
  "moulding: architrave", "moulding: door casing",
  "moulding: window casing", "moulding: wainscoting",
  "moulding: raised panel wainscoting", "moulding: flat panel wainscoting",
  "moulding: beadboard paneling", "moulding: v‑groove paneling",
  "moulding: shiplap", "moulding: nickel gap shiplap",
  "moulding: board and batten", "moulding: vertical board and batten",
  "moulding: grid paneling", "moulding: picture frame moulding",
  "moulding: boiserie paneling", "moulding: french boiserie",
  "moulding: wall paneling", "moulding: full‑height paneling",
  "moulding: applied moulding cabinet detail",
  "moulding: decorative end panel", "moulding: fluted wall panel",
  "moulding: reeded wall panel", "moulding: upholstered wall panel",
  "moulding: tufted wall panel", "moulding: leather wall panel",
  "moulding: acoustic slat wall panel", "moulding: 3D wall panel",
  "moulding: sculptural wall relief"
];

// --- FLOORING / RUG (labeled) ---
const FLOORING_LIBRARY = [
  "flooring: wide plank hardwood", "flooring: herringbone parquet",
  "flooring: chevron parquet", "flooring: versailles parquet",
  "flooring: basketweave parquet", "flooring: encaustic cement tile",
  "flooring: zellige tile", "flooring: terracotta tile",
  "flooring: penny round tile", "flooring: hexagon tile",
  "flooring: arabesque tile", "flooring: fish scale tile",
  "flooring: large format porcelain slab", "flooring: bookmatched marble floor",
  "flooring: polished concrete", "flooring: epoxy terrazzo",
  "flooring: limestone floor", "flooring: slate floor",
  "flooring: brick floor", "flooring: cobblestone",
  "flooring: sisal carpet", "flooring: wool carpet",
  "flooring: jute rug", "flooring: moroccan rug",
  "flooring: beni ourain rug", "flooring: kilim rug",
  "flooring: persian rug", "flooring: vintage overdyed rug",
  "flooring: cowhide rug", "flooring: sheepskin rug",
  "flooring: shag rug", "flooring: flatweave dhurrie",
  "flooring: braided jute rug", "flooring: patchwork rug",
  "flooring: layered runner", "flooring: bordered carpet"
];

// --- INDOOR PLANT (labeled) ---
const PLANT_LIBRARY = [
  "plant: fiddle leaf fig", "plant: monstera deliciosa",
  "plant: swiss cheese plant", "plant: philodendron",
  "plant: pothos", "plant: snake plant",
  "plant: ZZ plant", "plant: peace lily",
  "plant: spider plant", "plant: rubber plant",
  "plant: bird of paradise", "plant: dracaena marginata",
  "plant: corn plant", "plant: lucky bamboo",
  "plant: bamboo palm", "plant: parlor palm",
  "plant: areca palm", "plant: kentia palm",
  "plant: lady palm", "plant: fan palm",
  "plant: sago palm", "plant: ponytail palm",
  "plant: yucca cane", "plant: agave",
  "plant: aloe vera", "plant: haworthia",
  "plant: zebra plant", "plant: echeveria",
  "plant: burro's tail", "plant: string of pearls",
  "plant: string of bananas", "plant: string of hearts",
  "plant: jade plant", "plant: elephant bush",
  "plant: kalanchoe", "plant: air plant",
  "plant: spanish moss", "plant: staghorn fern",
  "plant: bird's nest fern", "plant: maidenhair fern",
  "plant: boston fern", "plant: asparagus fern",
  "plant: foxtail fern", "plant: croton",
  "plant: chinese evergreen", "plant: dieffenbachia",
  "plant: arrowhead plant", "plant: calathea orbifolia",
  "plant: calathea rattlesnake", "plant: prayer plant",
  "plant: stromanthe triostar", "plant: alocasia amazonica",
  "plant: alocasia zebrina", "plant: elephant ear",
  "plant: begonia maculata", "plant: african violet",
  "plant: moth orchid", "plant: dendrobium orchid",
  "plant: cattleya orchid", "plant: bromeliad",
  "plant: pink quill", "plant: anthurium",
  "plant: flamingo flower", "plant: hoya carnosa",
  "plant: sweetheart plant", "plant: lipstick plant",
  "plant: watermelon peperomia", "plant: chinese money plant",
  "plant: nerve plant", "plant: polka dot plant",
  "plant: wandering jew", "plant: purple heart",
  "plant: baby's tears", "plant: cast iron plant",
  "plant: bonsai ficus", "plant: juniper bonsai",
  "plant: olive tree indoor", "plant: citrus tree indoor",
  "plant: lemon tree", "plant: dwarf banana plant",
  "plant: norfolk island pine", "plant: umbrella tree",
  "plant: money tree", "plant: pencil cactus",
  "plant: crown of thorns", "plant: african milk tree",
  "plant: christmas cactus", "plant: orchid cactus",
  "plant: venus flytrap", "plant: pitcher plant",
  "plant: sundew", "plant: moss terrarium",
  "plant: living wall panel", "plant: hanging planter",
  "plant: macrame plant hanger", "plant: propagation station"
];

// --- HARDWARE / DETAIL (labeled) ---
const HARDWARE_LIBRARY = [
  "hardware: cabinet knob", "hardware: bar pull",
  "hardware: T‑bar pull", "hardware: finger pull",
  "hardware: edge pull", "hardware: cup pull",
  "hardware: bin pull", "hardware: latch",
  "hardware: concealed hinge", "hardware: exposed butt hinge",
  "hardware: decorative hinge", "hardware: brass nailhead trim",
  "hardware: nickel nailhead", "hardware: decorative stud",
  "hardware: leather pull", "hardware: recessed gola profile",
  "hardware: push‑to‑open latch", "hardware: touch latch",
  "hardware: soft‑close drawer slide", "hardware: dovetail drawer joint",
  "hardware: mullion cabinet insert", "hardware: wire mesh insert",
  "hardware: perforated metal insert"
];

// --- DECOR OBJECT / ACCESSORY (labeled) ---
const DECOR_LIBRARY = [
  "decor: oversized wall mirror", "decor: sunburst mirror",
  "decor: starburst mirror", "decor: antiqued mirror",
  "decor: venetian mirror", "decor: smoked mirror",
  "decor: bronze mirror", "decor: sculptural vase",
  "decor: bud vase", "decor: ceramic urn",
  "decor: glass cloche", "decor: terrarium",
  "decor: candle holder", "decor: candelabra",
  "decor: pillar candle", "decor: taper candle",
  "decor: decorative tray", "decor: catch‑all bowl",
  "decor: coffee table book", "decor: magazine rack",
  "decor: record player", "decor: vinyl display",
  "decor: framed art print", "decor: gallery wall",
  "decor: leaning artwork", "decor: sculptural object",
  "decor: marble bookend", "decor: geode specimen",
  "decor: bonsai display", "decor: ikebana arrangement",
  "decor: woven basket", "decor: throw blanket",
  "decor: accent pillow", "decor: lumbar pillow",
  "decor: bolster pillow", "decor: floor cushion",
  "decor: sheepskin throw", "decor: faux fur throw",
  "decor: wall tapestry", "decor: macramé wall hanging",
  "decor: mobile sculpture", "decor: kinetic sculpture",
  "decor: wall clock", "decor: mantel clock",
  "decor: barometer", "decor: globe",
  "decor: telescope", "decor: model sailboat",
  "decor: vintage camera", "decor: typewriter",
  "decor: bust sculpture", "decor: plaster cast",
  "decor: architectural fragment", "decor: corbel display",
  "decor: antique door knocker", "decor: found object art"
];

// ============================================
// COMBINED STYLE MODIFIERS (all categories merged)
// ============================================
const ADVANCED_STYLE_MODIFIERS = [
  ...QUALITY_MODIFIERS,
  ...LIGHTING_MODIFIERS,
  ...DESIGN_PRINCIPLES_MODIFIERS,
  ...COLOR_LIBRARY,
  ...TEXTURE_MATERIAL_LIBRARY,
  ...SOFA_SHAPE_LIBRARY,
  ...FURNITURE_TYPE_LIBRARY,
  ...LIGHTING_FIXTURE_LIBRARY,
  ...WINDOW_LIBRARY,
  ...DOOR_LIBRARY,
  ...CEILING_LIBRARY,
  ...MOULDING_LIBRARY,
  ...FLOORING_LIBRARY,
  ...PLANT_LIBRARY,
  ...HARDWARE_LIBRARY,
  ...DECOR_LIBRARY
];

// ============================================
// NEGATIVE PROMPTS (technical/spatial/aesthetic exclusions)
// ============================================
const NEGATIVE_TECHNICAL = [
  "blurry", "out of focus", "low resolution", "pixelated",
  "compression artifacts", "noise", "chromatic aberration",
  "lens flare", "overexposed", "underexposed", "harsh flash",
  "color cast", "white balance error", "oversaturated",
  "overprocessed HDR", "vignette", "motion blur"
];

const NEGATIVE_SPATIAL = [
  "distorted perspective", "warped perspective", "vanishing point error",
  "impossible geometry", "escher geometry", "tilted verticals",
  "converging parallels", "wonky horizon", "crooked",
  "skewed room proportions", "disproportionate", "incorrect scale",
  "misaligned architectural elements", "visible seams",
  "pattern mismatch", "broken pattern continuity"
];

const NEGATIVE_FURNITURE = [
  "floating furniture", "levitating", "missing legs",
  "unsupported structure", "extra limbs", "fused objects",
  "object merging", "undefined form", "double object",
  "object penetration", "clipping geometry", "deformed",
  "warped shape", "melted", "disconnected parts",
  "broken", "damaged", "dusty", "clutter", "overcrowded",
  "overdecorated", "overstyled"
];

const NEGATIVE_MATERIAL = [
  "plastic looking", "synthetic texture", "fake material",
  "rubbery", "cheap laminate", "flat lifeless surface",
  "no texture", "overly glossy", "incorrect reflectivity",
  "muddy texture", "blotchy", "repeating texture",
  "obvious pattern repeat", "wood grain error",
  "incorrect marble veining", "missing ambient occlusion",
  "floating shadow", "multiple conflicting shadows"
];

const NEGATIVE_LIGHTING = [
  "inconsistent lighting direction", "conflicting light sources",
  "impossible lighting", "source‑less illumination",
  "unnatural glow", "light leak through walls",
  "incorrect light color temperature", "mixed lighting temperatures",
  "flat ambient lighting", "no directional light",
  "shadowless", "window without exterior context",
  "blank window view", "day night confusion"
];

const NEGATIVE_STYLE = [
  "cartoon", "animation style", "illustration", "hand drawn",
  "sketch", "watercolor effect", "painterly", "oil painting",
  "digital art", "3D render low poly", "wireframe",
  "video game aesthetic", "CGI uncanny valley",
  "overly perfect synthetic", "uncanny valley", "fake looking",
  "stock photo generic", "dated design aesthetic",
  "outdated style cliché", "builder grade", "cheap",
  "sterile clinical environment", "hospital like",
  "corporate office", "airbnb generic", "staged home"
];

const NEGATIVE_DETAIL = [
  "missing architectural detail", "incomplete moulding",
  "simplified detail", "oversimplified", "lack of detail",
  "blurred detail", "indistinct", "missing hardware",
  "absent cabinet pulls", "inconsistent hardware finish",
  "mixed metals clash", "truncated furniture element",
  "cut off furniture", "unresolved joinery",
  "visible fasteners", "unfinished edge", "rough cut"
];

const NEGATIVE_CONTEXT = [
  "floating room", "missing walls", "open to void",
  "missing floor", "floating baseboard", "incomplete ceiling",
  "abrupt room boundary", "hard cutoff", "door to nowhere",
  "window to void", "window without exterior",
  "missing window sill", "floating architectural element",
  "unsupported arch", "non‑functional layout",
  "blocked circulation", "awkward furniture arrangement"
];

const NEGATIVE_TEXT = [
  "watermark", "copyright text", "signature on image",
  "logo visible", "URL", "social media handle",
  "timestamp overlay", "text on image", "lettering",
  "typography", "brand mark", "artist signature"
];

const NEGATIVE_CLUTTER = [
  "electrical cord visible", "power cord", "cable",
  "exposed wire", "power outlet visible", "light switch plate",
  "thermostat on wall", "air vent cover prominent",
  "ceiling fan", "television screen", "remote control",
  "smartphone", "microwave visible", "trash can",
  "cleaning supplies", "magazine clutter", "clothing item",
  "personal item clutter", "fingerprint visible",
  "water spot", "dust accumulation", "pet hair"
];

const NEGATIVE_DESIGN_ERRORS = [
  "disproportionate area rug", "rug too small",
  "incorrect rug placement", "undersized artwork",
  "artwork too high", "incorrect pendant height",
  "fixture too small", "improper curtain length",
  "curtain too short", "floating curtain rod",
  "misaligned tile", "uneven tile spacing",
  "mismatched wood tones", "clashing metal finishes",
  "color clash", "unharmonious palette",
  "mismatched undertones", "warm and cool clash"
];

// ============================================
// COMBINED NEGATIVE PROMPTS (all categories)
// ============================================
const ADVANCED_NEGATIVE_PROMPTS = [
  ...NEGATIVE_TECHNICAL,
  ...NEGATIVE_SPATIAL,
  ...NEGATIVE_FURNITURE,
  ...NEGATIVE_MATERIAL,
  ...NEGATIVE_LIGHTING,
  ...NEGATIVE_STYLE,
  ...NEGATIVE_DETAIL,
  ...NEGATIVE_CONTEXT,
  ...NEGATIVE_TEXT,
  ...NEGATIVE_CLUTTER,
  ...NEGATIVE_DESIGN_ERRORS
];

// ============================================
// BASIC FALLBACK (for testing/quick mode)
// ============================================
const BASIC_STYLE_MODIFIERS = [
  "photorealistic",
  "interior design magazine quality",
  "professional interior photography",
  "8k ultra high resolution",
  "natural lighting",
  "sharp focus",
  "highly detailed textures"
];

const BASIC_NEGATIVE_PROMPTS = [
  "blurry",
  "low quality",
  "distorted",
  "warped perspective",
  "floating furniture",
  "impossible geometry",
  "cartoon",
  "illustration",
  "watermark",
  "text overlay"
];

// ============================================
// SMART SELECTION: Pick top modifiers
// ============================================
const selectTopModifiers = (modifiers, count = 40) => {
  return modifiers.slice(0, count);
};

// ============================================
// EXPORT ADVANCED OR BASIC BASED ON TOGGLE
// ============================================
export const getEnhancePrompt = (basePrompt) => {
  if (USE_ADVANCED_PROMPTS) {
    const selectedStyle = selectTopModifiers(ADVANCED_STYLE_MODIFIERS, 50);
    const selectedNegative = selectTopModifiers(ADVANCED_NEGATIVE_PROMPTS, 50);
    return {
      enhancedPrompt: `${basePrompt}, ${selectedStyle.join(', ')}`,
      negativePrompt: selectedNegative.join(', ')
    };
  } else {
    return {
      enhancedPrompt: `${basePrompt}, ${BASIC_STYLE_MODIFIERS.join(', ')}`,
      negativePrompt: BASIC_NEGATIVE_PROMPTS.join(', ')
    };
  }
};